import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Checkbox } from '../../components/ui/checkbox';
import { clientService } from '../../services/clients/ClientService';
import {
  SuspensionsDueTodayContract,
  SuspensionsDueTodayLine,
} from '../../services/contracts/ContractLineService';
import { useToast } from '../../hooks/useToast';
import { extractApiErrorMessage } from '../../services/utils/apiErrorUtils';

type Props = {
  contracts: SuspensionsDueTodayContract[];
  selected: Set<string>;
  enqueueing: boolean;
  onToggleLine: (lineId: string, checked: boolean) => void;
  onEnqueueLine: (lineId: string) => void;
  onRetryBatch: (batchId: string) => void;
  retryingBatchId: string | null;
  onOpenDetails: (contractId: string) => void;
  onCredentialsSaved: () => void;
};

const linhaCritica = (line: SuspensionsDueTodayLine): boolean =>
  line.automationStatus === 'failed' || line.hasImpedimento || !line.temCredenciais;

const statusLinha = (line: SuspensionsDueTodayLine): string => {
  if (line.automationStatus === 'done') return 'Suspensa na automação';
  if (line.automationStatus === 'failed') return 'Falha na automação';
  if (line.automationStatus === 'pending' || line.automationStatus === 'processing') return 'Na fila';
  if (line.canEnqueue) return 'Pronta para suspender';
  return line.blockReason || 'Conferir';
};

const motivoLinha = (line: SuspensionsDueTodayLine): string =>
  line.motivoOperacionalLabel || line.blockReason || (line.kind === 'first_suspension' ? '1ª suspensão' : 'Ciclo 120 dias');

export const SuspensionsDueTodayList: React.FC<Props> = ({
  contracts,
  selected,
  enqueueing,
  onToggleLine,
  onEnqueueLine,
  onRetryBatch,
  retryingBatchId,
  onOpenDetails,
  onCredentialsSaved,
}) => {
  const rows = contracts.flatMap((contract) =>
    contract.lines.map((line) => ({ contract, line }))
  );

  return (
    <section className="rounded-lg border bg-white/80 overflow-hidden">
      <header className="px-4 py-3 border-b">
        <h2 className="text-base font-semibold">Contratos a suspender hoje</h2>
        <p className="text-sm text-muted-foreground">
          Uma lista para conferir e chamar a automação. Casos críticos só viram ticket se você abrir.
        </p>
      </header>
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b text-left text-muted-foreground">
              <th className="py-2 px-3 w-10" />
              <th className="py-2 pr-3">Cliente</th>
              <th className="py-2 pr-3">Contrato</th>
              <th className="py-2 pr-3">Linha</th>
              <th className="py-2 pr-3">Motivo</th>
              <th className="py-2 pr-3">Status</th>
              <th className="py-2 pr-3">E-mail Meu TIM</th>
              <th className="py-2 pr-3">Ações</th>
            </tr>
          </thead>
          <tbody>
            {rows.map(({ contract, line }) => (
              <tr key={line.lineId} className="border-b last:border-b-0 align-top">
                <td className="py-2 px-3">
                  <Checkbox
                    checked={selected.has(line.lineId)}
                    disabled={!line.canEnqueue}
                    onCheckedChange={(v) => onToggleLine(line.lineId, v === true)}
                    aria-label={`Selecionar linha ${line.numero ?? line.lineId}`}
                  />
                </td>
                <td className="py-2 pr-3">
                  <div className="font-medium">{contract.clientName}</div>
                  <div className="text-xs text-muted-foreground">{contract.clientCnpj}</div>
                </td>
                <td className="py-2 pr-3">{contract.contractNumber}</td>
                <td className="py-2 pr-3 whitespace-nowrap">
                  ({line.ddd}) {line.numero ?? '—'}
                  <div className="text-xs text-muted-foreground">{line.planName}</div>
                </td>
                <td className="py-2 pr-3">{motivoLinha(line)}</td>
                <td className="py-2 pr-3">
                  {statusLinha(line)}
                  {line.automationStatus === 'failed' && line.automationErrorMessage && (
                    <div className="text-xs text-red-700">{line.automationErrorMessage}</div>
                  )}
                </td>
                <td className="py-2 pr-3 min-w-[16rem]">
                  <MeuTimEmailFields
                    clientId={contract.clientId}
                    email={line.meuTimEmail}
                    temSenha={line.temCredenciais}
                    onSaved={onCredentialsSaved}
                  />
                </td>
                <td className="py-2 pr-3">
                  <div className="flex flex-col gap-1 items-start">
                    <Button
                      type="button"
                      size="sm"
                      disabled={!line.canEnqueue || enqueueing}
                      onClick={() => onEnqueueLine(line.lineId)}
                    >
                      Suspender
                    </Button>
                    {contract.batchId && contract.batchStatus === 'failed' && !line.protocolo && !contract.batchProtocolo && (
                      <Button
                        type="button"
                        size="sm"
                        variant="secondary"
                        disabled={retryingBatchId === contract.batchId}
                        onClick={() => onRetryBatch(contract.batchId!)}
                      >
                        Tentar de novo
                      </Button>
                    )}
                    <Button type="button" size="sm" variant="outline" onClick={() => onOpenDetails(contract.contractId)}>
                      Detalhes
                    </Button>
                    {linhaCritica(line) && (
                      <Link
                        className="text-xs underline"
                        to={`/pos-venda?novo=1&clientId=${contract.clientId}&contractId=${contract.contractId}&tipo=Suspensao`}
                      >
                        Abrir ticket
                      </Link>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </section>
  );
};

function MeuTimEmailFields({
  clientId,
  email,
  temSenha,
  onSaved,
}: {
  clientId: string;
  email?: string | null;
  temSenha: boolean;
  onSaved: () => void;
}) {
  const { showSuccess, showError } = useToast();
  const [login, setLogin] = useState(email ?? '');
  const [senha, setSenha] = useState('');
  const [mostrar, setMostrar] = useState(false);
  const [salvando, setSalvando] = useState(false);

  useEffect(() => {
    setLogin(email ?? '');
    setSenha('');
    setMostrar(false);
  }, [email, clientId]);

  const salvar = async () => {
    if (!login.trim() || !senha.trim()) {
      showError('Informe o e-mail e a senha do Meu TIM.');
      return;
    }
    setSalvando(true);
    try {
      await clientService.updateClientMeuTimCredentials(clientId, {
        meuTimLogin: login.trim(),
        meuTimSenha: senha,
      });
      setSenha('');
      setMostrar(false);
      showSuccess('E-mail do Meu TIM salvo no cliente.');
      onSaved();
    } catch (error: unknown) {
      showError(extractApiErrorMessage(error, 'Não foi possível salvar o e-mail do Meu TIM.'));
    } finally {
      setSalvando(false);
    }
  };

  return (
    <div className="flex flex-col gap-1">
      <input
        type="email"
        className="border rounded px-2 py-1 text-sm"
        value={login}
        autoComplete="off"
        placeholder="E-mail do cliente"
        aria-label="E-mail do Meu TIM"
        onChange={(e) => setLogin(e.target.value)}
      />
      <input
        type={mostrar ? 'text' : 'password'}
        className="border rounded px-2 py-1 text-sm"
        value={senha}
        autoComplete="new-password"
        placeholder={temSenha ? 'Senha cadastrada — preencha para trocar' : 'Senha do e-mail'}
        aria-label="Senha do e-mail Meu TIM"
        onChange={(e) => setSenha(e.target.value)}
      />
      <div className="flex gap-2">
        <button type="button" className="text-xs underline" onClick={() => setMostrar((v) => !v)}>
          {mostrar ? 'Ocultar senha' : 'Mostrar senha'}
        </button>
        <button type="button" className="text-xs underline" disabled={salvando} onClick={() => void salvar()}>
          {salvando ? <Loader2 className="w-3 h-3 animate-spin inline" /> : 'Salvar'}
        </button>
      </div>
    </div>
  );
}
